import type { TabsBlockAbstract, VariableADTvalue } from '@lighthouse/core'
import { TABS_TYPE } from '@lighthouse/core'
import { fieldConvertValue, flexMinSizeStyle, getCursorStyle, useApplicationContext, useFlowLayoutContext } from '@lighthouse/shared'
import { useUncontrolled } from '@lighthouse/tools'
import React, { forwardRef, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { AnimationDecorators } from '../../components'
import { useEffectiveAnchorId } from '../../context'
import { useCustomViewBlockContext } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'
import { Tabs } from './Tabs'

const Root = styled(AnimationDecorators)`
    width: 100%;
    height: 100%;
    padding: var(--block-padding);
`

interface TabsBlockProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'value' | 'onChange'> {
    blockData: TabsBlockAbstract
    scale?: number
    value?: string
    defaultIndex?: number
    onChange?: (value: string) => void
    onClickTab?: (e: React.MouseEvent<HTMLButtonElement>, id: string) => void
}

const TabsBlock = forwardRef<HTMLDivElement, TabsBlockProps>(
    ({ scale, blockData, value, defaultIndex, onChange, onClickTab, ...rest }, ref) => {
        const { config, id, title } = blockData
        const { variantMap, variant, baseList, breakPoint, cursor, anchor } = config
        const { anchorId } = anchor || {}
        const effectiveAnchorId = useEffectiveAnchorId(anchorId)
        useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
        const { font, design, size, animation } = breakPoint
        const { styleList, align } = variantMap[variant]
        const index = defaultIndex ?? 0
        const [_value, _onChange] = useUncontrolled({ value, onChange, defaultValue: baseList?.[index]?.id })

        const { appId = '' } = useApplicationContext()

        const list = useMemo(() => {
            return baseList.map(item => {
                const style = styleList.find(s => s.id === item.id)
                return {
                    ...item,
                    ...style
                }
            })
        }, [baseList, styleList])

        const [imageVariables, colorVariables] = useMemo(() => {
            const images: VariableADTvalue[] = []
            const colors: VariableADTvalue[] = []

            // 收集tab
            list.forEach(c => {
                // 收集背景图片变量
                if (c.background?.image?.resource) {
                    images.push(c.background.image.resource)
                }
                if (c.active?.background?.image?.resource) {
                    images.push(c.active.background.image.resource)
                }
                if (c.hover?.background?.image?.resource) {
                    images.push(c.hover.background.image.resource)
                }

                // 收集背景色变量
                if (c.background?.variable) {
                    colors.push(c.background.variable)
                }
                if (c.active?.background?.variable) {
                    colors.push(c.active.background.variable)
                }
                if (c.hover?.background?.variable) {
                    colors.push(c.hover.background.variable)
                }
            })

            // 收集tabs container
            if (design?.background?.image?.resource) {
                images.push(design.background.image.resource)
            }
            if (design?.background?.variable) {
                colors.push(design.background.variable)
            }

            // 收集tabs indicator
            if (variant === TABS_TYPE.LINE && design?.lineColor?.variable) {
                colors.push(design.lineColor.variable)
            }

            return [images, colors]
        }, [list, design.background?.image?.resource, design.background?.variable, design.lineColor?.variable, variant])

        const { useParseVariableWithSubscription } = useFlowLayoutContext()
        const parseImageVariable = useParseVariableWithSubscription(imageVariables)
        const parseColorVariable = useParseVariableWithSubscription(colorVariables)

        const { record } = useCustomViewBlockContext()

        const parseBackgroundImageVariable = useCallback(
            (value: VariableADTvalue) => {
                const field = parseImageVariable(value, {
                    viewRecord: record
                })

                return field ? fieldConvertValue(field, { useFileUrl: true }) : ''
            },
            [parseImageVariable, record]
        )

        const parseBackgroundColorVariable = useCallback(
            (value: VariableADTvalue) => {
                const field = parseColorVariable(value, {
                    viewRecord: record
                })

                return field ? fieldConvertValue(field) : ''
            },
            [parseColorVariable, record]
        )

        return (
            <Root
                ref={ref}
                {...rest}
                blockId={id}
                blockName={title}
                animation={animation}
                id={effectiveAnchorId}
                style={{ ...flexMinSizeStyle, overflow: size.overflow, ...rest.style, ...getCursorStyle(appId, cursor) }}
            >
                <Tabs
                    mode={variant}
                    design={design}
                    align={align}
                    scale={scale}
                    list={list}
                    font={font}
                    value={_value}
                    onChange={_onChange}
                    onClickTab={onClickTab}
                    parseVariableImage={parseBackgroundImageVariable}
                    parseVariableColor={parseBackgroundColorVariable}
                    data-stop-action-propagation
                />
            </Root>
        )
    }
)

export default React.memo(TabsBlock)
